import { CloudUpload } from "@mui/icons-material";
import { Box, Button, Typography, styled } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "text/csv",
  "text/plain",
  "application/pdf",
  "video/mp4",
  "video/webm",
  "video/quicktime",
];

const ACCEPT_ATTR = "image/*, .csv, .txt, .pdf, video/mp4, video/webm, video/quicktime";

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragging",
})(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  transition: "border-color 0.2s ease, background-color 0.2s ease",
  backgroundColor: isDragging
    ? theme.palette.action.hover
    : theme.palette.background.paper,
  "&:hover": {
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.action.hover,
  },
}));

const FileUpload = ({ enableDragAndDrop, loading, handleFiles }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Clipboard paste support
  useEffect(() => {
    const handlePaste = (event) => {
      const pastedFiles = Array.from(event.clipboardData.items)
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter(Boolean);

      if (pastedFiles.length > 0) processFiles(pastedFiles);
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  const processFiles = (files) => {
    setError(null);
    const validFiles = files.filter(
      (file) => ALLOWED_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE
    );

    if (validFiles.length < files.length) {
      setError(
        `Only .jpg, .png, .csv, .txt, .pdf, and video (.mp4, .webm, .mov) files under ${MAX_FILE_SIZE / 1024 / 1024}MB are allowed.`
      );
      return;
    }

    handleFiles(validFiles);
  };

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop      = (e) => { e.preventDefault(); setIsDragging(false); processFiles(Array.from(e.dataTransfer.files)); };
  const handleFileChange = (e) => processFiles(Array.from(e.target.files));
  const handleChooseFiles = () => fileInputRef.current.click();

  if (loading) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", py: 2 }}
      >
        Uploading…
      </Typography>
    );
  }

  return (
    <Box>
      {enableDragAndDrop ? (
        <DropZone
          isDragging={isDragging}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleChooseFiles}
          mb={2.5}
        >
          <CloudUpload sx={{ fontSize: 28, color: "text.disabled", mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            Drag &amp; drop, click, or paste to upload files
          </Typography>
          <Typography variant="caption" color="text.disabled">
            JPG, PNG, CSV, TXT, PDF, MP4, WEBM, MOV — max {MAX_FILE_SIZE / 1024 / 1024} MB
          </Typography>
        </DropZone>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={handleChooseFiles}
          sx={{ mb: 1.5 }}
        >
          Attach files
        </Button>
      )}

      <input
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        onChange={handleFileChange}
        style={{ display: "none" }}
        ref={fileInputRef}
      />

      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FileUpload;